import React, { useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';

import { getBuildConfig, getLogger, tracker, httpClientInterface, getEnvironmentConfig } from 'companion-app-components/utils/core';
import { profileActions, profileSelectors } from 'companion-app-components/flux/profile';
import { authSelectors } from 'companion-app-components/flux/auth';

import { useFetchOnce } from 'companion-app-components/hooks/utils';
import { useGlobalCobrowseRequired } from 'utils/cobrowse';
import { getCurrentThemeKey } from 'themes/themesSelectors';

const log = getLogger('utils/intercom.js');
const APP_ID = getBuildConfig().intercom_app_id;

const Intercom = React.memo((_props) => {
  const profile = useSelector(profileSelectors.getProfile);
  const authSession = useSelector(authSelectors.getAuthSession);
  const themeKey = useSelector(getCurrentThemeKey);
  const intercomJwtRef = useRef();

  const [, setCobrowseRequired] = useGlobalCobrowseRequired();

  useFetchOnce(profileActions.getProfile, profileSelectors, false, true);

  useEffect(() => {
    /* eslint-disable */
    if (APP_ID && authSession.qcsId) {
      (async function () {
        let intercomUserJWT;
        try {
          const response = await httpClientInterface.send(
            'GET',
            `${getEnvironmentConfig().services_url}/jwt-sso/intercom_user_jwt`,
            undefined,
            { 'qcs-dataset-id': authSession?.datasetId || null }
          );
          intercomUserJWT = response.data?.intercom_user_jwt;
          intercomJwtRef.current = intercomUserJWT;
        } catch (error) {
          assert(false, error);
        }
        //Set your APP_ID
        try {
          (function () {
            var w = window;
            var ic = w.Intercom;
            if (typeof ic === 'function') {
              ic('reattach_activator');
              ic('update', w.intercomSettings);
            } else {
              var d = document;
              var i = function () {
                i.c(arguments);
              };
              i.q = [];
              i.c = function (args) {
                i.q.push(args);
              };
              w.Intercom = i;
              var l = function () {
                var s = d.createElement('script');
                s.type = 'text/javascript';
                s.async = true;
                s.src = 'https://widget.intercom.io/widget/' + APP_ID;
                var x = d.getElementsByTagName('script')[0];
                x.parentNode.insertBefore(s, x);
              };
              if (document.readyState === 'complete') {
                l();
              } else if (w.attachEvent) {
                w.attachEvent('onload', l);
              } else {
                w.addEventListener('load', l, false);
              }
            }
          })();

          window.intercomSettings = {
            app_id: APP_ID,
            intercom_user_jwt: intercomUserJWT,
            custom_launcher_selector: '#intercom-launcher',
            hide_default_launcher: true,
          }
          updateIntercom();
        } catch (error) {
          log.error(error);
        }
      })();
    } else {
      try {
        window.Intercom?.('shutdown');
      } catch (error) {
        assert(false, error);
      }
    }
  }, [authSession]);

  const updateIntercom = useCallback(() => {
    try {
      if (window.Intercom) {
        const name = [...(profile?.firstName ? [profile.firstName] : []), ...(profile?.lastName ? [profile.lastName] : [])].join(' ');
        const intercomUserJWT = intercomJwtRef.current;
        window.Intercom('boot', {
          app_id: APP_ID,
          intercom_user_jwt: intercomUserJWT,
          custom_launcher_selector: '#intercom-launcher',
          hide_default_launcher: true,
          theme_mode: themeKey === 'dark' ? 'dark' : 'default',
        });
        if (intercomUserJWT) {
          window.Intercom('setAuthTokens', {
            data_connector_jwt: intercomUserJWT,
          });
        }
        window.Intercom('onShow', () => {
          tracker.track(tracker.events.showIntercom);
          // enable co-browse
          setCobrowseRequired(true);
        });
        window.Intercom('update', {
          intercom_user_jwt: intercomUserJWT,
          user_id: authSession?.qcsId,
          qcs_id: authSession?.qcsId,
          dataset_id: authSession?.datasetId,
          scope: authSession?.scope,
          groups: authSession?.groups,
          email: profile?.username,
          name,
          theme_mode: themeKey === 'dark' ? 'dark' : 'default',
        });
        if (intercomUserJWT) {
          window.Intercom('setAuthTokens', {
            data_connector_jwt: intercomUserJWT,
          });
        }
      }
    } catch (e) {
      assert(false, e);
    }

    return () => window.Intercom?.('shutdown');
  }, [authSession, profile, themeKey]);

  useEffect(() => {
    updateIntercom();
  }, [profile, authSession, updateIntercom]);

  return null;
});

export default Intercom;
